import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, ApiError } from '~/lib/api';
import { cn } from '~/lib/cn';
import {
  FUNDING_MODES,
  FUNDING_MODE_COPY,
  type FundingMode,
  type Product,
  type Program,
} from '~/lib/programs';
import type { Account } from '~/lib/accounts';
import { NewAccountPanel } from '~/components/accounts/NewAccountPanel';
import { SlideOver } from '~/components/ui/SlideOver';

interface NewProgramPanelProps {
  open: boolean;
  onClose: () => void;
  onCreated: (program: Program) => void;
}

/**
 * Three-field program creation panel. The design doc (dashboard-ia §5)
 * calls fundingMode out as a radio surfaced here, not buried in settings —
 * once a program is sealed it's immutable, and the operator needs to see
 * the trade-off before they pick. We render the two options as radio cards
 * with the summary inline.
 *
 * `delegated` requires `customerWebhookUrl`; the client guards for it before
 * sending so a misclick doesn't round-trip for a 400.
 */
export function NewProgramPanel({
  open,
  onClose,
  onCreated,
}: NewProgramPanelProps): JSX.Element {
  const [displayName, setDisplayName] = useState('');
  const [products, setProducts] = useState<Product[] | null>(null);
  const [productId, setProductId] = useState<string>('');
  const [productsError, setProductsError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<string>('');
  const [newAccountOpen, setNewAccountOpen] = useState(false);
  const [fundingMode, setFundingMode] = useState<FundingMode>('prefunded');
  const [customerWebhookUrl, setCustomerWebhookUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedProduct = useMemo(
    () => products?.find((p) => p.id === productId) ?? null,
    [products, productId],
  );
  // Only accounts whose currency matches the picked product can fund it;
  // the backend rejects a mismatch at submit with FUNDING_ACCOUNT_CURRENCY_MISMATCH.
  const eligibleAccounts = useMemo(
    () =>
      selectedProduct
        ? (accounts ?? []).filter(
            (a) => a.currency === selectedProduct.currency,
          )
        : [],
    [accounts, selectedProduct],
  );

  async function loadAccounts(): Promise<void> {
    try {
      setAccountsError(null);
      const res = await api<{ data: Account[] }>('/dashboard/accounts');
      setAccounts(res.data);
    } catch (err) {
      setAccountsError(
        err instanceof ApiError
          ? err.message
          : 'Could not load funding accounts.',
      );
    }
  }

  useEffect(() => {
    if (!open) return;
    // Fresh load on each open; catalog is tiny.
    let active = true;
    (async () => {
      try {
        setProductsError(null);
        const res = await api<{ data: Product[] }>('/dashboard/products');
        if (!active) return;
        setProducts(res.data);
        if (res.data.length > 0 && !productId) setProductId(res.data[0]!.id);
      } catch (err) {
        if (!active) return;
        setProductsError(
          err instanceof ApiError
            ? err.message
            : 'Could not load the product catalog.',
        );
      }
    })();
    void loadAccounts();
    return () => {
      active = false;
    };
  }, [open, productId]);

  // When the product changes, clear the account pick if it no longer matches
  // the new product's currency — otherwise the submit would 400.
  useEffect(() => {
    if (!accountId) return;
    const match = eligibleAccounts.some((a) => a.id === accountId);
    if (!match) setAccountId('');
  }, [accountId, eligibleAccounts]);

  function reset(): void {
    setDisplayName('');
    setFundingMode('prefunded');
    setCustomerWebhookUrl('');
    setAccountId('');
    setError(null);
    setSubmitting(false);
    // Leave products + accounts cached — next open repopulates.
  }

  function closeAndReset(): void {
    reset();
    onClose();
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (displayName.trim() === '') {
      setError('Give the program a display name — what should the operator see later?');
      return;
    }
    if (!productId) {
      setError('Pick a product (card network + currency).');
      return;
    }
    if (fundingMode === 'delegated' && customerWebhookUrl.trim() === '') {
      setError(
        'Delegated routing sends every authorization to your service — the webhook URL is required.',
      );
      return;
    }
    if (fundingMode === 'prefunded' && !accountId) {
      setError(
        'A prefunded program debits one of your funding accounts — pick one, or add a new one.',
      );
      return;
    }
    setSubmitting(true);
    try {
      const res = await api<Program>('/dashboard/programs', {
        method: 'POST',
        body: {
          productId,
          displayName: displayName.trim(),
          fundingMode,
          ...(fundingMode === 'prefunded' && { accountId }),
          ...(fundingMode === 'delegated' && {
            customerWebhookUrl: customerWebhookUrl.trim(),
          }),
        },
      });
      onCreated(res);
      closeAndReset();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not create the program. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SlideOver
      open={open}
      onClose={closeAndReset}
      title="New program"
      description="A set of cards governed by one authorization model. Sealed on first use."
      footer={
        <div className="flex flex-col gap-2">
          {error && (
            <div
              role="alert"
              className="rounded border border-stop/20 bg-stop-tint px-3 py-2 text-[13.5px] leading-[18px] text-stop"
            >
              {error}
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={closeAndReset}
              className="h-9 rounded-md px-3 text-[14px] font-medium text-ink-secondary hover:bg-surface-margin hover:text-ink-primary"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="new-program-form"
              disabled={submitting || !products}
              className="inline-flex h-9 items-center justify-center rounded-md bg-ink-primary px-4 text-[14px] font-semibold text-ink-on-dark hover:bg-ink-primary/90 disabled:opacity-60"
            >
              {submitting ? 'Creating…' : 'Create program'}
            </button>
          </div>
        </div>
      }
    >
      <form id="new-program-form" onSubmit={onSubmit} className="flex flex-col gap-6">
        <Field label="Display name" hint="Shown in the dashboard and on the authorization log.">
          <input
            autoFocus
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="payroll-ngn"
            className="h-9 w-full rounded border border-rule-divider bg-surface-margin px-3 text-[14.5px] text-ink-primary focus:border-ink-primary focus:bg-surface-slip focus:outline-none focus:ring-2 focus:ring-ink-primary/15"
          />
        </Field>

        <section>
          <SectionLabel
            label="Product"
            hint="Network + currency. Each card issued under this program inherits both."
          />
          {!products && !productsError && (
            <div className="rounded border border-rule-hairline bg-surface-margin px-3 py-2 text-[13.5px] text-ink-watermark">
              Loading product catalog…
            </div>
          )}
          {productsError && (
            <div className="rounded border border-stop/20 bg-stop-tint px-3 py-2 text-[13.5px] text-stop">
              {productsError}
            </div>
          )}
          {products && products.length === 0 && (
            <div className="rounded border border-notice/30 bg-notice-tint px-3 py-2 text-[13.5px] text-notice">
              No products in the catalog yet. Ask the Seven team to seed one before continuing.
            </div>
          )}
          {products && products.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {products.map((p) => (
                <ProductRow
                  key={p.id}
                  product={p}
                  checked={productId === p.id}
                  onPick={() => setProductId(p.id)}
                />
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionLabel
            label="Funding mode"
            hint="How each authorization decision is made. Immutable after the first card is issued."
          />
          <div className="flex flex-col gap-2">
            {FUNDING_MODES.map((mode) => (
              <FundingCard
                key={mode}
                mode={mode}
                checked={fundingMode === mode}
                onPick={() => setFundingMode(mode)}
              />
            ))}
          </div>
        </section>

        {fundingMode === 'prefunded' && (
          <section>
            <div className="mb-2 flex items-end justify-between gap-3">
              <SectionLabel
                label="Funding account"
                hint={
                  selectedProduct
                    ? `Which ${selectedProduct.currency} pool this program debits. Must match the product currency.`
                    : 'Pick a product first — the picker only lists accounts with a matching currency.'
                }
              />
              <button
                type="button"
                onClick={() => setNewAccountOpen(true)}
                className="inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-medium text-ink-primary hover:underline"
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.75}
                  strokeLinecap="round"
                  aria-hidden
                >
                  <path d="M8 3v10M3 8h10" />
                </svg>
                New account
              </button>
            </div>
            {accountsError && (
              <div className="rounded border border-stop/20 bg-stop-tint px-3 py-2 text-[13.5px] text-stop">
                {accountsError}
              </div>
            )}
            {!accountsError && !accounts && (
              <div className="rounded border border-rule-hairline bg-surface-margin px-3 py-2 text-[13.5px] text-ink-watermark">
                Loading accounts…
              </div>
            )}
            {!accountsError &&
              accounts &&
              eligibleAccounts.length === 0 && (
                <div className="rounded border border-notice/30 bg-notice-tint px-3 py-2 text-[13.5px] leading-[18px] text-notice">
                  {selectedProduct
                    ? `No ${selectedProduct.currency} funding accounts yet. Use "New account" above to add one — it stays in context.`
                    : 'No funding accounts yet.'}
                </div>
              )}
            {!accountsError && eligibleAccounts.length > 0 && (
              <div className="flex flex-col gap-1.5">
                {eligibleAccounts.map((a) => (
                  <AccountRow
                    key={a.id}
                    account={a}
                    checked={accountId === a.id}
                    onPick={() => setAccountId(a.id)}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {fundingMode === 'delegated' && (
          <Field
            label="Customer auth webhook"
            hint="Seven POSTs every authorization to this URL and takes your decision. Must respond within ~1 second."
          >
            <input
              type="url"
              value={customerWebhookUrl}
              onChange={(e) => setCustomerWebhookUrl(e.target.value)}
              placeholder="https://api.yourservice.ng/authorize"
              className="h-9 w-full rounded border border-rule-divider bg-surface-margin px-3 text-[14.5px] text-ink-primary focus:border-ink-primary focus:bg-surface-slip focus:outline-none focus:ring-2 focus:ring-ink-primary/15"
            />
          </Field>
        )}
      </form>

      <NewAccountPanel
        open={newAccountOpen}
        onClose={() => setNewAccountOpen(false)}
        onCreated={(a) => {
          // Preselect the newly created account if it matches the current
          // product currency, so the operator can submit without hunting.
          setAccounts((prev) => (prev ? [a, ...prev] : [a]));
          if (!selectedProduct || a.currency === selectedProduct.currency) {
            setAccountId(a.id);
          }
        }}
      />
    </SlideOver>
  );
}

function AccountRow({
  account,
  checked,
  onPick,
}: {
  account: Account;
  checked: boolean;
  onPick: () => void;
}): JSX.Element {
  const refLabel = account.externalAccountRef
    ? account.externalAccountRef
    : 'Provisioning…';
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={checked}
      className={cn(
        'flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors',
        checked
          ? 'border-ink-primary bg-ink-primary/[0.03]'
          : 'border-rule-divider bg-surface-slip hover:border-ink-primary/40',
      )}
    >
      <div className="min-w-0">
        <div className="truncate text-[14px] font-semibold text-ink-primary">
          {account.label || '(unlabelled)'}
        </div>
        <div className="font-mono mt-0.5 truncate text-[11.5px] text-ink-watermark">
          {account.currency} · {refLabel}
        </div>
      </div>
      <Radio checked={checked} />
    </button>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[13.5px] font-medium text-ink-primary">{label}</span>
      {hint && <span className="text-[11.5px] text-ink-watermark">{hint}</span>}
      {children}
    </label>
  );
}

function SectionLabel({ label, hint }: { label: string; hint?: string }): JSX.Element {
  return (
    <div className="mb-2">
      <div className="text-[13.5px] font-medium text-ink-primary">{label}</div>
      {hint && <div className="text-[11.5px] text-ink-watermark">{hint}</div>}
    </div>
  );
}

function ProductRow({
  product,
  checked,
  onPick,
}: {
  product: Product;
  checked: boolean;
  onPick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={checked}
      className={cn(
        'flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-left transition-colors',
        checked
          ? 'border-ink-primary bg-ink-primary/[0.03]'
          : 'border-rule-divider bg-surface-slip hover:border-ink-primary/40',
      )}
    >
      <div>
        <div className="text-[14px] font-semibold text-ink-primary">
          {product.name}
        </div>
        <div className="font-mono mt-0.5 text-[11.5px] text-ink-watermark">
          {product.network} · {product.currency} · {product.category}
        </div>
      </div>
      <Radio checked={checked} />
    </button>
  );
}

function FundingCard({
  mode,
  checked,
  onPick,
}: {
  mode: FundingMode;
  checked: boolean;
  onPick: () => void;
}): JSX.Element {
  const copy = FUNDING_MODE_COPY[mode];
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={checked}
      className={cn(
        'flex items-start justify-between gap-3 rounded-md border px-3 py-3 text-left transition-colors',
        checked
          ? 'border-ink-primary bg-ink-primary/[0.03]'
          : 'border-rule-divider bg-surface-slip hover:border-ink-primary/40',
      )}
    >
      <div>
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-semibold text-ink-primary">
            {copy.label}
          </span>
          <span className="font-mono rounded bg-surface-margin px-1.5 py-[1px] text-[10.5px] text-ink-secondary">
            {mode}
          </span>
        </div>
        <div className="mt-0.5 text-[13px] font-medium text-ink-secondary">
          {copy.summary}
        </div>
        <div className="mt-1 text-[11.5px] text-ink-watermark">
          {copy.detail}
        </div>
      </div>
      <Radio checked={checked} />
    </button>
  );
}

function Radio({ checked }: { checked: boolean }): JSX.Element {
  return (
    <span
      className={cn(
        'mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full border',
        checked ? 'border-ink-primary' : 'border-rule-divider',
      )}
      aria-hidden
    >
      {checked && <span className="h-2 w-2 rounded-full bg-ink-primary" />}
    </span>
  );
}
